/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IDictionary } from '@msdyn365-commerce/core';
import { CommerceProperty, SalesOrdersDataActions } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';

import { CheckInConfirmationComponent } from './check-in-confirmation';
import { CheckInErrorComponent } from './check-in-error';
import { IAdditionalInformationKeysData, ICheckInForPickupConfig, ICheckInForPickupProps } from './check-in-for-pickup.props.autogenerated';

/**
 * The interface representing a piece of additional information data.
 */
interface IAdditionalInformation {
    formKey: IAdditionalInformationKeysData;
    value: string;
}

/**
 * The state for check-in view.
 */
export interface ICheckInViewState {

    /**
     * If the module has header error to show.
     */
    isHeaderError: boolean;

    /**
     * If the customer has checked in.
     */
    isCheckedIn: boolean;

    /**
     * Additional information to capture.
     */
    additionalInformation: IDictionary<string>[];
}

/**
 * The properties for check-in view.
 */
export interface ICheckInViewProps extends ICheckInForPickupProps<ICheckInForPickupConfig> {
    headerError: React.ReactNode;
    confirmationComponent: React.ReactNode;
    viewState: ICheckInViewState;
    additionalInformationValues: string[];
    checkInForOrderPickup(additionalInformation?: IAdditionalInformation[]): Promise<void>;
}

/**
 * The state of the CheckInForPickup module.
 */
export interface ICheckInForPickupState {

    /**
     * The header error message.
     */
    headerErrorMessage: string;

    /**
     * If the customer has checked in successfully.
     */
    isCheckedIn: boolean;
}

/**
 *
 * CheckIn component.
 * @extends {React.Component<ICheckInForPickupProps<ICheckInForPickupConfig>, ICheckInForPickupState>}
 */
class CheckInForPickup extends React.Component<ICheckInForPickupProps<ICheckInForPickupConfig>, ICheckInForPickupState> {

    public constructor(props: ICheckInForPickupProps<ICheckInForPickupConfig>) {
        super(props);
        this.state = {
            headerErrorMessage: '',
            isCheckedIn: false
        };
    }

    public render(): JSX.Element | null {
        const isHeaderError: boolean = this.state.headerErrorMessage !== '';

        const viewProps = {
            ...this.props,
            headerError: <CheckInErrorComponent errorMessage={this.state.headerErrorMessage} />,
            confirmationComponent: <CheckInConfirmationComponent
                confirmationText={this.props.resources.defaultConfirmationText}
                shouldShowQrCode={this.props.config.shouldShowQrCode as boolean}
                channelReferenceIdLabel={this.props.resources.confirmationIdLabel}
                channelReferenceId={this.props.context.request.query?.channelReferenceId} />,
            checkInForOrderPickup: this.checkInForOrderPickup,
            additionalInformationValues: this.props.config.additionalInformationKeys ? this.props.config.additionalInformationKeys.map(() => {
                return {};
            }) : undefined,
            viewState: {
                isHeaderError,
                isCheckedIn: this.state.isCheckedIn
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public shouldComponentUpdate(nextProps: ICheckInForPickupProps<ICheckInForPickupConfig>, nextState: ICheckInForPickupState): boolean {
        if (this.props.config.additionalInformationHeading !== nextProps.config.additionalInformationHeading ||
            this.props.config.confirmationText !== nextProps.config.confirmationText) {

            return true;
        }
        if (this.state.headerErrorMessage !== nextState.headerErrorMessage ||
            this.state.isCheckedIn !== nextState.isCheckedIn) {
            return true;
        }
        return false;
    }

    public async componentDidMount(): Promise<void> {
        await this._onInit();
    }

    /**
     * The async callback for submitting check-in.
     * @param additionalInformationList - The optional additional information captured.
     * @returns The async result.
     */
    public checkInForOrderPickup = async (additionalInformationList?: IAdditionalInformation[]): Promise<void> => {
        const channelId: number = Number.parseInt(this.props.context.request.query!.channelId, 10);
        const channelReferenceId: string = this.props.context.request.query!.channelReferenceId;
        const packingSlipId: string = this.props.context.request.query!.packingSlipId;

        const additionalInformationToSubmit: { formKey: string; value: string }[] | undefined =
            additionalInformationList?.map(additionalInformationKeyValuePair => {
                return { formKey: additionalInformationKeyValuePair.formKey.resourceId ?? '', value: additionalInformationKeyValuePair.value };
            });
        const extensionPropertyList: CommerceProperty[] = [];
        if (!ObjectExtensions.isNullOrUndefined(additionalInformationToSubmit)) {
            for (const additionalInformation of additionalInformationToSubmit) {
                extensionPropertyList.push({
                    Key: additionalInformation.formKey,
                    Value: {
                        StringValue: additionalInformation.value
                    }
                });
            }
        }
        try {
            await SalesOrdersDataActions.checkInForOrderPickupAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                channelId,
                packingSlipId,
                channelReferenceId,
                extensionPropertyList);
        } catch (error) {
            this.state = { headerErrorMessage: this.props.resources.genericErrorMessage, isCheckedIn: false };
            this.props.telemetry.error(error);
            return Promise.resolve();
        }
        this.setState({ headerErrorMessage: '', isCheckedIn: true });
        return Promise.resolve();
    };

    /**
     * The action performed on module initialization.
     * @returns The async result.
     */
    private readonly _onInit = async (): Promise<void> => {
        const channelId: number = Number.parseInt(this.props.context.request.query?.channelId ?? '', 10);
        const channelReferenceId: string | undefined = this.props.context.request.query?.channelReferenceId;
        const packingSlipId: string | undefined = this.props.context.request.query?.packingSlipId;
        if (Number.isNaN(channelId) || !channelReferenceId || !packingSlipId) {
            // No error message shown in editor mode.
            if (this.props.context.request.params.isEditor) {
                return Promise.resolve();
            } else if (this.props.context.request.params.isPreview) {
                this.setState({ headerErrorMessage: this.props.resources.requiredParameterMissingErrorMessage });
            } else {
                this.setState({ headerErrorMessage: this.props.resources.genericErrorMessage });
            }
            this.props.context.telemetry.error('One or more required URL parameters are missing. ' +
                'Please make sure you have pass in channelId, channelReferenceId and packingSlipId through URL parameter.');
            return Promise.resolve();
        }

        const {
            additionalInformationKeys
        } = this.props.config;

        const hasAdditionalInformation: boolean = ArrayExtensions.hasElements(additionalInformationKeys);

        // Call check-in-for-pickup directly if no additional information to capture.
        if (!hasAdditionalInformation) {
            try {
                await SalesOrdersDataActions.checkInForOrderPickupAsync(
                    { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                    channelId,
                    packingSlipId,
                    channelReferenceId);
            } catch (error) {
                this.setState({ headerErrorMessage: this.props.resources.genericErrorMessage, isCheckedIn: false });
                this.props.telemetry.error(error);
                return Promise.resolve();
            }
            this.setState({ headerErrorMessage: '', isCheckedIn: true });
        }
        return Promise.resolve();
    };
}

export default CheckInForPickup;
